/**
 * Main Product — Variant Picker
 *
 * Powers variant selection inside the custom product section.
 *
 * Features:
 *  - Swatch / button pickers
 *  - Dropdown pickers
 *  - Thumbnail gallery switching
 *  - URL variant syncing  (?variant=ID)
 *  - Hidden <select> syncing for form submit & theme compatibility
 */

(function () {
    "use strict";

    const jQuery = window.jQuery;
    if (!jQuery) return;

    // ─── Debug flag ───────────────────────────────────────────────────────────
    // Set to `true` while developing to log picker activity to the console.
    const DEBUG = false;

    // ─── DOM selectors ────────────────────────────────────────────────────────
    // Centralised here so any template changes only need one edit.
    const SELECTOR = {
        section: "[data-heth]",
        variantJson: "[data-heth-json]",
        variantSelect: "[data-heth-variant-select]",
        price: "[data-heth-price]",
        comparePrice: "[data-heth-compare]",
        addToCartButton: "[data-heth-atc]",
        pickerButton: "[data-heth-picker]",
        pickerSelect: "[data-heth-picker-select]",
        optionValue: "[data-heth-option-val]",
        slide: "[data-slide]",
        thumb: "[data-heth-thumb]",
    };

    // Kick off once the DOM is ready.
    jQuery(initAllProductSections);

    // ─── Initialisation ───────────────────────────────────────────────────────

    /** Find every product section on the page and initialise each one. */
    function initAllProductSections() {
        jQuery(SELECTOR.section).each(function () {
            initSingleSection(jQuery(this));
        });
    }

    /**
     * Bootstrap a single product section.
     * Guards against double-initialisation via a data flag.
     *
     * @param {jQuery} $section - The product section wrapper element.
     */
    function initSingleSection($section) {
        // Skip if this section has already been set up.
        if ($section.data("bDevsInit") === true) return;
        $section.data("bDevsInit", true);

        // Read variant data embedded in the page as JSON.
        const $jsonNode = $section.find(SELECTOR.variantJson).first();
        if (!$jsonNode.length) return;

        const variantData = JSON.parse($jsonNode.text());
        const variants = variantData.variants || [];
        const moneyFormat = variantData.money_format || null;
        if (!variants.length) return;

        // Cache frequently used jQuery objects.
        const $variantSelect = $section.find(SELECTOR.variantSelect).first();
        const $price = $section.find(SELECTOR.price).first();
        const $comparePrice = $section.find(SELECTOR.comparePrice).first();
        const $addToCartButton = $section
            .find(SELECTOR.addToCartButton)
            .first();
        const $productVariantIdInput = $section.find(
            '[data-heth-product-variant-id][name="product-variant-id"]',
        );

        /**
         * Tracks the currently selected option values.
         * Index 0 → Option 1 (e.g. Colour), index 1 → Option 2 (e.g. Size), etc.
         *
         * @type {string[]}
         */
        let selectedOptions = [];
        let isSyncingHiddenVariantSelect = false;

        // ─── Utilities ────────────────────────────────────────────────────────

        /** Log a message to the console only when DEBUG is enabled. */
        function logDebug(message, payload) {
            if (!DEBUG) return;
            console.log(`[heth] ${message}`, payload || "");
        }

        /** Return the `variant` query-parameter value from the current URL. */
        function getUrlVariantId() {
            return new URLSearchParams(window.location.search).get("variant");
        }

        /**
         * Read an element attribute and parse it as an integer.
         *
         * @param   {jQuery} $element      - Target element.
         * @param   {string} attributeName - The HTML attribute to read.
         * @returns {number}
         */
        function parseIntAttr($element, attributeName) {
            return parseInt($element.attr(attributeName), 10);
        }

        /**
         * Convert a price in cents to the store's money format.
         * Uses Shopify formatter when available, with Intl fallback.
         *
         * @param   {number} cents
         * @returns {string}
         */
        function formatMoney(cents) {
            const amountInCents = Number(cents || 0);

            if (
                window.Shopify &&
                typeof window.Shopify.formatMoney === "function"
            ) {
                const fallbackFormat = "${{amount}}";
                const resolvedMoneyFormat =
                    moneyFormat ||
                    (window.theme && window.theme.moneyFormat) ||
                    window.Shopify.money_format ||
                    fallbackFormat;

                return window.Shopify.formatMoney(
                    amountInCents,
                    resolvedMoneyFormat,
                );
            }

            const currencyCode =
                window.Shopify &&
                window.Shopify.currency &&
                window.Shopify.currency.active
                    ? window.Shopify.currency.active
                    : "USD";

            try {
                return new Intl.NumberFormat(undefined, {
                    style: "currency",
                    currency: currencyCode,
                    currencyDisplay: "narrowSymbol",
                }).format(amountInCents / 100);
            } catch (error) {
                return (amountInCents / 100).toFixed(2);
            }
        }

        // ─── Variant lookup ───────────────────────────────────────────────────

        /**
         * Find a variant by its numeric or string ID.
         *
         * @param   {string|number} variantId
         * @returns {object|null}
         */
        function findVariantById(variantId) {
            if (!variantId) return null;
            return (
                variants.find(
                    (variant) => String(variant.id) === String(variantId),
                ) || null
            );
        }

        /**
         * Find the first variant whose options array matches the supplied array exactly.
         *
         * @param   {string[]} options - e.g. ['Red', 'Large']
         * @returns {object|null}
         */
        function findVariantByOptions(options) {
            return (
                variants.find((variant) =>
                    variant.options.every(
                        (value, index) => value === options[index],
                    ),
                ) || null
            );
        }

        /**
         * Determine which variant to show on first load, using the following priority:
         *  1. Variant ID set directly on the section element
         *  2. Variant ID in the URL query string
         *  3. Variant ID in the hidden <select> element
         *  4. First variant in the list
         *
         * @returns {object}
         */
        function getInitialVariant() {
            const sectionVariantId = $section.attr("data-initial-variant-id");
            const sectionVariant = findVariantById(sectionVariantId);
            if (sectionVariant) return sectionVariant;

            const urlVariant = findVariantById(getUrlVariantId());
            if (urlVariant) return urlVariant;

            const hiddenSelectVariant = findVariantById($variantSelect.val());
            if (hiddenSelectVariant) return hiddenSelectVariant;

            return variants[0];
        }

        // ─── Gallery helpers ──────────────────────────────────────────────────

        /**
         * Collect all media / image IDs that are associated with a variant.
         * Shopify exposes this through several different keys depending on the
         * theme setup, so we try every known location and drop any blanks.
         *
         * @param   {object} variant
         * @returns {string[]}
         */
        function getVariantImageIds(variant) {

            return [
                variant?.featured_media?.id,
                variant?.featured_media?.preview_image?.id,
                variant?.featured_image?.id,
                variant?.featured_image?.media_id,
                variant?.image_id,
            ]
                .filter(Boolean)
                .map(String);
        }

        // ─── DOM sync helpers ─────────────────────────────────────────────────

        /**
         * Push the selected variant ID into the browser's address bar
         * without triggering a full page reload.
         *
         * @param {object} variant
         */
        function syncUrlVariant(variant) {
            const url = new URL(window.location.href);
            const current = url.searchParams.get("variant");
            const next = String(variant.id);

            if (current === next) return; // Nothing to update.

            url.searchParams.set("variant", next);
            window.history.replaceState({}, "", url.toString());
        }

        /**
         * Keep the hidden native <select> in sync so the add-to-cart form
         * submits the correct variant and other theme scripts stay compatible.
         *
         * @param {object} variant
         */
        function syncHiddenVariantSelect(variant) {
            if (!$variantSelect.length) return;

            const variantId = String(variant.id);
            const currentVariantId = String($variantSelect.val() || "");

            if (currentVariantId === variantId) return;

            isSyncingHiddenVariantSelect = true;
            $variantSelect.val(variantId);

            // Explicitly mark the matching <option> as selected for older browsers.
            $variantSelect
                .find(`option[value="${variantId}"]`)
                .prop("selected", true);

            $variantSelect.trigger("change");
            isSyncingHiddenVariantSelect = false;
        }

        /**
         * Keep hidden `product-variant-id` input synced with current variant.
         *
         * @param {object} variant
         */
        function syncHiddenProductVariantIdInput(variant) {
            if (!$productVariantIdInput.length) return;
            $productVariantIdInput.val(String(variant.id));
        }

        /**
         * Refresh the displayed price and compare-at price for the chosen variant.
         *
         * @param {object} variant
         */
        function updatePriceBlock(variant) {
            if ($price.length) {
                $price.text(formatMoney(variant.price));
            }

            if (!$comparePrice.length) return;

            const hasComparePrice = variant.compare_at_price > variant.price;
            $comparePrice.css("display", hasComparePrice ? "" : "none");

            if (hasComparePrice) {
                $comparePrice.text(formatMoney(variant.compare_at_price));
            }
        }

        /**
         * Disable or re-enable the add-to-cart button and update its label
         * to reflect the variant's availability.
         *
         * @param {object} variant
         */
        function updateAddToCartBlock(variant) {
            if (!$addToCartButton.length) return;

            const isAvailable = Boolean(variant && variant.available);

            $addToCartButton.prop("disabled", !isAvailable);
            if (isAvailable) {
                $addToCartButton.removeAttr("disabled");
            } else {
                $addToCartButton.attr("disabled", "disabled");
            }

            $addToCartButton.text(
                isAvailable
                    ? $addToCartButton.data("addLabel")
                    : $addToCartButton.data("unavailableLabel"),
            );
        }

        /**
         * Update any inline text nodes that display the current value of an option
         * (e.g. "Colour: Red" labels next to a swatch picker).
         */
        function syncOptionValueText() {
            $section.find(SELECTOR.optionValue).each(function () {
                const $label = jQuery(this);
                const optionIndex = parseIntAttr(
                    $label,
                    "data-heth-option-val",
                );
                if (isNaN(optionIndex)) return;

                $label.text(selectedOptions[optionIndex]);
            });
        }

        /**
         * Mark one gallery slide (and its matching thumbnail) as active,
         * removing the active state from all others.
         *
         * @param {jQuery} $slides      - All slide elements.
         * @param {jQuery} $thumbs      - All thumbnail elements.
         * @param {number} activeIndex  - Zero-based index of the slide to show.
         */
        function setActiveGalleryIndex($slides, $thumbs, activeIndex) {
            $slides.each(function (index, slide) {
                const isActive = index === activeIndex;
                const $slide = jQuery(slide);
                const $thumb = $thumbs.eq(index);

                $slide.toggleClass("is-active", isActive);

                if ($thumb.length) {
                    $thumb.toggleClass("is-active", isActive);
                    $thumb.attr("aria-selected", isActive ? "true" : "false");
                }
            });
        }

        /**
         * When the variant changes, scroll / snap the gallery to the slide that
         * shows the variant's featured image.  Falls back to slide 0 if no match.
         *
         * @param {object} variant
         */
        function syncGalleryForVariant(variant) {
            const $slides = $section.find(SELECTOR.slide);
            const $thumbs = $section.find(SELECTOR.thumb);
            if (!$slides.length) return;

            const variantImageIds = getVariantImageIds(variant);
            if (!variantImageIds.length) return;

            let matchedIndex = -1;

            $slides.each(function (index, slide) {
                const $slide = jQuery(slide);
                const mediaId = String($slide.attr("data-media-id") || "");
                const imageId = String($slide.attr("data-image-id") || "");
                const isMatch =
                    variantImageIds.includes(mediaId) ||
                    variantImageIds.includes(imageId);

                // Keep the first matched index; ignore subsequent matches.
                if (isMatch && matchedIndex === -1) {
                    matchedIndex = index;
                }
            });

            setActiveGalleryIndex(
                $slides,
                $thumbs,
                matchedIndex === -1 ? 0 : matchedIndex,
            );
        }

        /**
         * Reflect the current selectedOptions array in all picker controls:
         *  - Toggle `is-selected` on swatch / button pickers.
         *  - Set the value of dropdown pickers.
         */
        function syncPickerControls() {
            // Swatch / button pickers.
            $section.find(SELECTOR.pickerButton).each(function () {
                const $button = jQuery(this);
                const optionIndex = parseIntAttr($button, "data-option-index");
                const buttonValue = $button.attr("data-heth-picker");
                const isSelected = buttonValue === selectedOptions[optionIndex];

                $button.toggleClass("is-selected", isSelected);
            });

            // Dropdown pickers.
            $section.find(SELECTOR.pickerSelect).each(function () {
                const $select = jQuery(this);
                const optionIndex = parseIntAttr($select, "data-option-index");
                $select.val(selectedOptions[optionIndex]);
            });
        }

        // ─── Core render ──────────────────────────────────────────────────────

        /**
         * Apply all UI updates for a given variant in one call.
         * This is the single function that should be called after any variant change.
         *
         * @param {object|null} variant
         */
        function renderVariant(variant) {
            if (!variant) return;

            updatePriceBlock(variant);
            updateAddToCartBlock(variant);
            syncHiddenVariantSelect(variant);
            syncHiddenProductVariantIdInput(variant);
            syncUrlVariant(variant);
            syncGalleryForVariant(variant);
            syncOptionValueText();
        }

        // ─── Variant resolution logic ─────────────────────────────────────────

        /**
         * After the shopper picks a new option value, work out which variant
         * should now be active.
         *
         * Resolution order:
         *  1. Exact match on all current selectedOptions.
         *  2. An available variant that at least matches the changed option.
         *  3. Any variant that matches the changed option (sold out is OK).
         *
         * When a fallback is used, selectedOptions is updated and the picker
         * controls are re-synced so the UI stays consistent.
         *
         * @param   {number} optionIndex  - Which option position changed (0-based).
         * @param   {string} optionValue  - The new value chosen.
         * @returns {object|null}
         */
        function resolveVariantAfterSelection(optionIndex, optionValue) {
            selectedOptions[optionIndex] = optionValue;

            // 1. Perfect match.
            const exactMatch = findVariantByOptions(selectedOptions);
            if (exactMatch) return exactMatch;

            // 2. Same option, but prefer an available variant.
            const availableFallback = variants.find(
                (variant) =>
                    variant.options[optionIndex] === optionValue &&
                    variant.available,
            );
            if (availableFallback) {
                selectedOptions = availableFallback.options.slice();
                syncPickerControls();
                return availableFallback;
            }

            // 3. Same option, availability doesn't matter.
            const firstFallback = variants.find(
                (variant) => variant.options[optionIndex] === optionValue,
            );
            if (firstFallback) {
                selectedOptions = firstFallback.options.slice();
                syncPickerControls();
                return firstFallback;
            }

            return null;
        }

        /**
         * Set a variant as the active one, updating both the UI and internal state.
         *
         * @param {object|null} variant
         */
        function applyVariant(variant) {
            if (!variant) return;
            selectedOptions = variant.options.slice();
            syncPickerControls();
            renderVariant(variant);
        }

        // ─── Event handlers ───────────────────────────────────────────────────

        /** Handle clicks on swatch / button pickers. */
        function onPickerButtonClick() {
            const $button = jQuery(this);
            const optionIndex = parseIntAttr($button, "data-option-index");
            const optionValue = $button.attr("data-heth-picker");

            logDebug("picker button click", {
                optionIndex,
                optionValue,
                element: this,
            });

            // Deselect all buttons for this option, then select the clicked one.
            $section
                .find(
                    `${SELECTOR.pickerButton}[data-option-index="${optionIndex}"]`,
                )
                .removeClass("is-selected");
            $button.addClass("is-selected");

            const nextVariant = resolveVariantAfterSelection(
                optionIndex,
                optionValue,
            );
            renderVariant(nextVariant);
        }

        /** Handle changes to dropdown pickers. */
        function onPickerSelectChange() {
            const $select = jQuery(this);
            const optionIndex = parseIntAttr($select, "data-option-index");
            const optionValue = $select.val();

            logDebug("picker select change", {
                optionIndex,
                optionValue,
                element: this,
            });

            const nextVariant = resolveVariantAfterSelection(
                optionIndex,
                optionValue,
            );
            renderVariant(nextVariant);
        }

        /** Handle clicks on gallery thumbnails to manually switch the active slide. */
        function onThumbClick() {
            const activeIndex = parseIntAttr(jQuery(this), "data-heth-thumb");
            const $slides = $section.find(SELECTOR.slide);
            const $thumbs = $section.find(SELECTOR.thumb);
            setActiveGalleryIndex($slides, $thumbs, activeIndex);
        }

        // ─── Event binding ────────────────────────────────────────────────────

        /** Attach all event listeners for this section. */
        function bindEvents() {
            $section.on("click", SELECTOR.pickerButton, onPickerButtonClick);
            $section.on("change", SELECTOR.pickerSelect, onPickerSelectChange);
            $section.find(SELECTOR.thumb).on("click", onThumbClick);

            // If any other script updates the hidden variant select,
            // keep this section UI (including ATC state) in sync.
            $variantSelect.on("change", function () {
                if (isSyncingHiddenVariantSelect) return;

                const variantFromSelect = findVariantById(jQuery(this).val());
                if (!variantFromSelect) return;
                selectedOptions = variantFromSelect.options.slice();
                syncPickerControls();
                renderVariant(variantFromSelect);
            });

            // Re-apply any URL variant once all resources have loaded
            // (handles cases where other scripts mutate the DOM after DOMContentLoaded).
            jQuery(window).on("load", function () {
                applyVariant(findVariantById(getUrlVariantId()));
            });
        }

        // ─── Bootstrap ────────────────────────────────────────────────────────

        bindEvents();

        // Apply the initial variant, then check the URL again in case the
        // initial variant and the URL variant differ.
        applyVariant(getInitialVariant());
        applyVariant(findVariantById(getUrlVariantId()));
    }
})();
